import { useTranslation } from 'react-i18next'
import { ConfirmDialog } from '@/components/confirm-dialog'
import {
  resolveSectionRegenerateDisclaimerMode,
  type ComplianceDocumentRegenerateDocumentType,
  type SectionRegenerateDisclaimerMode,
} from '../utils/resolveSectionRegenerateDisclaimerMode'

type SectionRegenerateConfirmDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  sectionId: string | null
  sectionTitle: string | null
  sectionKind?: 'canonical' | 'custom'
  documentType: ComplianceDocumentRegenerateDocumentType
  translationKeyPrefix: 'manualGenerationPage' | 'riskAssessmentPage'
  onConfirm: (sectionId: string) => void
  isLoading?: boolean
}

function resolveDisclaimerDescriptionKey(
  mode: SectionRegenerateDisclaimerMode
): string {
  return `sectionActions.regenerateConfirmDialog.${mode}`
}

export function SectionRegenerateConfirmDialog({
  open,
  onOpenChange,
  sectionId,
  sectionTitle,
  sectionKind,
  documentType,
  translationKeyPrefix,
  onConfirm,
  isLoading = false,
}: SectionRegenerateConfirmDialogProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')

  if (sectionId == null) {
    return null
  }

  const disclaimerMode = resolveSectionRegenerateDisclaimerMode({
    documentType,
    sectionId,
    sectionKind,
  })
  const confirmDisabled = disclaimerMode === 'customUnsupported'
  const sectionLabel =
    sectionTitle != null && sectionTitle.trim().length > 0
      ? sectionTitle.trim()
      : sectionId

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      handleConfirm={() => {
        onConfirm(sectionId)
      }}
      title={t(`${translationKeyPrefix}.sectionActions.regenerateConfirmDialog.title`, {
        section: sectionLabel,
      })}
      desc={t(
        `${translationKeyPrefix}.${resolveDisclaimerDescriptionKey(disclaimerMode)}`,
        { section: sectionLabel }
      )}
      cancelBtnText={t(
        `${translationKeyPrefix}.sectionActions.regenerateConfirmDialog.cancel`
      )}
      confirmText={t(
        `${translationKeyPrefix}.sectionActions.regenerateConfirmDialog.confirm`
      )}
      destructive={!confirmDisabled}
      disabled={confirmDisabled}
      isLoading={isLoading}
    />
  )
}
